import React, { useState } from "react";
import { Alert, Badge, Box, Button, Group, Text, Tooltip } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { FaExternalLinkAlt, FaInfoCircle } from "react-icons/fa";
import { usePostMultiplacerDevicesSimilarRoomsWithIfcDesignQuery } from "../../../../openapi";
import { useAppSelector } from "../../../app/hooks";
import { getPercentColor } from "../../../RoomColoring";
import {
  selectCachedSimilarRoomsWithIfcDesign,
  selectSimilarRoomsWithIfcDesignCurrentRoomName,
  selectSimilarRoomsWithIfcDesignCurrentRoomNumber,
  selectSimilarRoomsWithIfcDesignQueryArgs,
  selectSimilarRoomsWithIfcDesignSortedBySimilarity,
} from "./similarRoomsWithIfcDesignSelectors";
import { SimilarRoomsWithIfcDesignModal } from "./SimilarRoomsWithIfcDesignModal";

const getSimilarityPercent = (score: number): number => Math.round(score * 100);
const formatSimilarity = (score: number): string => `${getSimilarityPercent(score)} %`;
const compactBestMatches = (
  rooms: { roomNumber: string; similarityScore: number; ifcDeviceCount: number }[]
): string =>
  rooms
    .slice(0, 3)
    .map(
      (match) =>
        `${match.roomNumber} (${formatSimilarity(match.similarityScore)}, ${match.ifcDeviceCount} IFC)`
    )
    .join(", ");

export const SimilarRoomsWithIfcDesignBanner: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const roomNumber = useAppSelector(selectSimilarRoomsWithIfcDesignCurrentRoomNumber);
  const roomName = useAppSelector(selectSimilarRoomsWithIfcDesignCurrentRoomName);
  const args = useAppSelector(selectSimilarRoomsWithIfcDesignQueryArgs);

  const { isError, isFetching } = usePostMultiplacerDevicesSimilarRoomsWithIfcDesignQuery(
    args ?? skipToken
  );

  const matchingRooms = useAppSelector(selectCachedSimilarRoomsWithIfcDesign);
  const sortedMatchingRooms = useAppSelector(selectSimilarRoomsWithIfcDesignSortedBySimilarity);

  if (!args || !roomNumber || !roomName) {
    return null;
  }

  if (isFetching) {
    return (
      <Alert variant="light" color="blue" icon={<FaInfoCircle size={14} />} py="xs">
        <Text size="xs" truncate="end">
          Sjekker om lignende rom er prosjektert i IFC...
        </Text>
      </Alert>
    );
  }

  if (isError) {
    return null;
  }

  if (matchingRooms.length === 0) {
    return (
      <Alert variant="light" color="gray" icon={<FaInfoCircle size={14} />} py="xs">
        <Text size="xs" truncate="end">
          Ingen lignende rom med belysning eller luftdon i IFC kunne hittes.
        </Text>
      </Alert>
    );
  }

  const topSimilarityPercent = getSimilarityPercent(sortedMatchingRooms[0]?.similarityScore ?? 0);
  const topSimilarityColor = getPercentColor(topSimilarityPercent);
  const titleText = `${matchingRooms.length} lignende rom har belysning eller luftdon i IFC`;
  const titleTooltip = `${matchingRooms.length} lignende ${roomName}-rom har belysning eller luftdon i IFC`;
  const bestMatchesText = sortedMatchingRooms
    .map(
      (match) =>
        `${match.roomNumber} (${formatSimilarity(match.similarityScore)}, ${match.ifcDeviceCount} IFC-komponenter)`
    )
    .join(", ");
  const compactBestMatchesText = compactBestMatches(sortedMatchingRooms);

  return (
    <Alert
      variant="light"
      icon={<FaInfoCircle size={14} />}
      py="xs"
      styles={{
        root: {
          backgroundColor: `${topSimilarityColor}1A`,
          borderColor: topSimilarityColor,
          paddingBlock: 8,
          paddingInline: 12,
        },
        icon: {
          color: topSimilarityColor,
        },
        body: {
          minWidth: 0,
        },
      }}
    >
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 8,
          alignItems: "center",
          minWidth: 0,
        }}
      >
        <Box style={{ minWidth: 0 }}>
          <Tooltip label={titleTooltip} multiline maw={360} openDelay={300}>
            <Text size="xs" fw={600} truncate="end">
              {titleText}
            </Text>
          </Tooltip>
          <Tooltip label={`Beste treff: ${bestMatchesText}`} multiline maw={360} openDelay={300}>
            <Text size="xs" c="dimmed" truncate="end" style={{ minWidth: 0 }}>
              Beste treff: {compactBestMatchesText}
            </Text>
          </Tooltip>
        </Box>
        <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
          <Badge
            size="sm"
            variant="filled"
            styles={{
              root: {
                backgroundColor: topSimilarityColor,
                color: "#ffffff",
                minWidth: 58,
              },
            }}
          >
            {topSimilarityPercent} %
          </Badge>
          <Button
            size="xs"
            variant="filled"
            color="dark"
            px={8}
            leftSection={<FaExternalLinkAlt size={11} />}
            onClick={() => setModalOpen(true)}
          >
            Vis rom
          </Button>
        </Group>
      </Box>
      {modalOpen && <SimilarRoomsWithIfcDesignModal onClose={() => setModalOpen(false)} />}
    </Alert>
  );
};
